<template>
  <v-dialog v-model="activator"
            :fullscreen="fullscreen"
            :persistent="persistent"
            :width="dialogSize"
            v-bind="attrs.componentAttrs"
  >
    <!-- Most of the time, the other slots should be used -->
    <!-- This is useful for components that explicitly need to fully control the template -->
    <slot name="default">
      <v-card :class="{'background-grey': backgroundGrey}" :data-locator="attrs.dataLocator">
        <!-- STEPPER -->
        <template v-if="stepperDialog">
          <AppDialogStepperTemplate :model-value="stepperModel"
                                    :stepper-items="stepperItems"
                                    :close-button="closeButton"
                                    :cancel-disabled="cancelDisabled"
                                    @cancel="cancel"
          >
            <template v-for="(_, slot) in $slots" v-slot:[slot]>
              <slot :name="slot"/>
            </template>
          </AppDialogStepperTemplate>
        </template>
        <!-- TABS -->
        <template v-else-if="tabsDialog">
          <AppDialogTabsTemplate :close-button="closeButton"
                                 :tabs-template-has-title="tabsTemplateHasTitle"
                                 :cancel-disabled="cancelDisabled"
                                 @cancel="cancel"
          >
            <template v-for="(_, slot) in $slots" v-slot:[slot]>
              <slot :name="slot" />
            </template>
          </AppDialogTabsTemplate>
        </template>
        <template v-else>
          <v-card-title
            v-if="!hideHeader"
            :class="[
              headerColor,
              { 'text-white': headerDark },
              { 'app-dialog__title--divider': headerDivider && !$slots.subTitle },
            ]"
          >
            <!-- @slot for title content -->
            <div class="text-truncate w-100" data-locator="dialog-title">
              <slot name="title"></slot>
            </div>
            <AppButtonLegacy
              v-if="closeButton"
              class="close-button mb-auto"
              :class="{'text-white': headerDark, 'mt-auto': fullscreen}"
              data-locator="close-button"
              :disabled="cancelDisabled"
              icon
              variant="text"
              density="comfortable"
              tabindex="-1"
              @click="cancel"
            >
              <font-awesome-icon :icon="['fal', 'xmark']" size="lg"/>
            </AppButtonLegacy>
          </v-card-title>
          <v-card-subtitle
            v-if="$slots.subTitle"
            data-locator="dialog-sub-title"
            :class="{ 'app-dialog__subtitle--divider': headerDivider }"
          >
            <!-- @slot for sub title content -->
            <slot name="subTitle"></slot>
          </v-card-subtitle>
          <v-card-text data-locator="dialog-content">
            <!-- @slot for body content -->
            <slot name="body"></slot>
          </v-card-text>
        </template>
        <v-card-actions v-if="!hideFooter && !$slots.footer" class="pb-5">
          <v-spacer></v-spacer>
          <slot name="footer-infos"></slot>
          <!--
              triggered on click
              @event click
            -->
          <app-button
            v-if="!okOnly"
            type="outlined"
            variant="neutral"
            :disabled="cancelDisabled"
            :loading="cancelLoading"
            data-locator="cancel-button"
            @click="cancel"
          >
            {{ computedCancelText }}
          </app-button>
          <!--
              triggered on click
              @event click
            -->
          <app-button
            v-if="!cancelOnly"
            :disabled="okDisabled"
            :loading="okLoading"
            data-locator="confirm-button"
            @click="ok"
          >
            {{ computedOkText }}
          </app-button>
        </v-card-actions>
        <v-card-actions v-if="$slots.footer">
          <slot name="footer"></slot>
        </v-card-actions>
      </v-card>
    </slot>
  </v-dialog>
</template>

<script>
import AppButtonLegacy from '@/common/buttons/AppButton.vue'
import AppDialogStepperTemplate from '@/common/dialogs/AppDialogStepperTemplate.vue'
import AppDialogTabsTemplate from '@/common/dialogs/AppDialogTabsTemplate.vue'
import { useDataLocator } from '@/common/utils/useDataLocator'

const DIALOG_SIZES = Object.freeze({
  s: '200',
  m: '400',
  l: '600',
  xl: '800',
  xxl: '900',
  xxxl: '1200',
  auto: 'auto',
  'dialog-xs': '480',
  'dialog-sm': '640',
})

export const MODAL_FADE_TRANSITION_TIME = 300

/**
 * Default dialog
 * @displayName Dialog
 */
export default {
  /**
   * All Vuetify props for Dialog components are usable
   * @see See doc : https://vuetifyjs.com/en/api/v-dialog/
   */
  name: 'AppDialog',
  components: {
    AppButtonLegacy,
    AppDialogStepperTemplate,
    AppDialogTabsTemplate,
  },
  inheritAttrs: false,
  setup () {
    const { attrs } = useDataLocator()
    return { attrs }
  },
  props: {
    /**
     * Set loading state of the cancel button
     */
    cancelLoading: {
      type: Boolean,
      default: false,
    },
    /**
     * Removes the ability to click or target the cancel button
     */
    cancelDisabled: {
      type: Boolean,
      default: false,
    },
    /**
     * Text displayed inside the cancel button
     */
    cancelText: {
      type: String,
      default: undefined,
    },
    /**
     * @model
     * Visibility state of the dialog
     */
    isOpen: {
      type: Boolean,
      default: false,
    },
    /**
     * Removes the ability to click or target the ok button
     */
    okDisabled: {
      type: Boolean,
      default: false,
    },
    /**
     * Set loading state of the ok button
     */
    okLoading: {
      type: Boolean,
      default: false,
    },
    /**
     * Ok button will not be displayed if set to true
     */
    cancelOnly: {
      type: Boolean,
      default: false,
    },
    /**
     * Cancel button will not be displayed if set to true
     */
    okOnly: {
      type: Boolean,
      default: false,
    },
    /**
     * Text displayed inside the ok button
     */
    okText: {
      type: String,
    },
    /**
     * Clicking outside of the element will not deactivate it
     */
    persistent: {
      type: Boolean,
      default: true,
    },
    fullscreen: {
      type: Boolean,
      default: false,
    },
    /**
     * Set width of dialog
     * @values s (200px), m (400px, default), l (600px), xl (800px), auto
     */
    size: {
      type: String,
      validator: value => {
        return Object.keys(DIALOG_SIZES).includes(value)
      },
      default: 'm',
    },
    hideHeader: {
      type: Boolean,
      default: false,
    },
    hideFooter: {
      type: Boolean,
      default: false,
    },
    /**
     * If close button is displayed
     */
    closeButton: {
      type: Boolean,
      default: true,
    },
    /**
     * Enable Stepper template
     */
    stepperDialog: {
      type: Boolean,
      default: false,
    },
    /**
     * Enable Tabs template
     */
    tabsDialog: {
      type: Boolean,
      default: false,
    },
    /**
     * Enable title above v-tabs header
     */
     tabsTemplateHasTitle: {
      type: Boolean,
      default: false,
    },
    /**
     * The designated model value for the stepper.
     */
    stepperModel: {
      default: undefined,
    },
    /**
     * The stepper steps.
     */
    stepperItems: {
      type: Array,
      default: () => [],
    },
    /**
     * Set card text background to #f5f5f5
     */
    backgroundGrey: {
      type: Boolean,
      default: false,
    },
    /**
     * Set card header background color
     */
    headerColor: {
      type: String,
      default: 'white',
    },
    /**
     * Set card header with dark prop
     */
    headerDark: {
      type: Boolean,
      default: false,
    },
    /**
     * Set card header with a divider
     */
    headerDivider: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    activator: {
      get () {
        return this.isOpen
      },
      set (val) {
        this.$emit('update:isOpen', val)
      },
    },
    computedCancelText () {
      return this.cancelText ?? this.$t('common.cancel')
    },
    computedOkText () {
      return this.okText ?? this.$t('common.validate')
    },
    dialogSize () {
      return this.fullscreen ? undefined : DIALOG_SIZES[this.size]
    },
  },
  methods: {
    /**
     * Gets called when the user clicks on the cancel button
     * @event cancel
     * @type void
     */
    cancel () {
      if (!this.cancelDisabled) {
        this.$emit('cancel')
      }
    },
    /**
     * Gets called when the user clicks on the cancel button
     * @event ok
     * @type void
     */
    ok () {
      if (!this.okLoading && !this.okDisabled) {
        this.$emit('ok')
      }
    },
  },
}
</script>

<style scoped lang="scss">
@import "vuetify/settings";

.v-dialog {
  .v-card {
    position: relative;

    &-title {
      position: relative;
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      flex-wrap: nowrap;
      padding: 24px 24px 10px;

      .close-button {
        margin-top: -12px;
      }

      &.app-dialog__title--divider {
        border-bottom: 1px solid rgb(var(--v-theme-grey-100));
      }
    }
    &-subtitle {
      padding: 10px 24px 20px;
      margin-top: -10px;

      &.app-dialog__subtitle--divider {
        border-bottom: 1px solid rgb(var(--v-theme-grey-100));
      }
    }

    &-text {
      padding: 0 24px 12px;
      color: rgb(var(--v-theme-grey-800));
      letter-spacing: normal !important;
      line-height: normal !important;
    }

    &-actions {
      position: sticky;
      z-index: 1;
      bottom: 0;
      width: 100%;
      background-color: #fff;
      padding: 8px 24px 24px;

      :deep(.v-btn) {
        padding: 0 16px;
      }
    }

    &.background-grey {
      background-color: #f5f5f5;
    }
  }
}

@media #{map-get($display-breakpoints, 'xs')} {
  .v-dialog {
    .v-card {
      &-title {
        padding: 12px 12px 10px !important;
      }

      &-text {
        padding: 0 12px 20px !important;
      }

      &-actions {
        padding: 8px 12px !important;
      }
    }
  }
}

.v-dialog--fullscreen .v-card-title {
  position: sticky !important;
  top: 0;
  z-index: 1;
  padding-bottom: 24px !important;
}
</style>
